import Table from '@eduzz/houston-ui/Table';
import Typography from '@eduzz/houston-ui/Typography';
import moment from 'moment';
import React, { memo, useCallback } from 'react';
import { FormattedNumber } from 'react-intl';
import { useCallbackObservable } from 'react-use-observable';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import Truncate from 'v2/components/Shared/TruncateText';
import subscriptionsService from 'v2/services/subscriptions';

import type { ISubscriptionInvoice, ISubscriptions } from 'v2/interfaces/subscriptions';

import { useStyles } from '../styles';
import { PaymentImg } from './PaymentImg';

interface IProps {
  subscription: ISubscriptions;
  handleSetCurrentInvoice: (invoice: ISubscriptionInvoice) => void;
}

export const CollapseInvoice = memo(({ subscription, handleSetCurrentInvoice }: IProps) => {
  const classes = useStyles();

  const [getInvoicesByContractId, invoices] = useCallbackObservable((id: number) => subscriptionsService.getInvoicesByContractId(id), []);

  const onCollapseHandler = useCallback(
    (subscription: ISubscriptions) => {
      getInvoicesByContractId(subscription.id);
    },
    [getInvoicesByContractId]
  );

  return (
    <Table.Collapse onOpen={onCollapseHandler} disabled={false}>
      <Table total={invoices?.length} loading={invoices === null}>
        <Table.Header>
          <Table.Column width={200}>Data Vencimento</Table.Column>
          <Table.Column width={110}>Fatura</Table.Column>
          <Table.Column width={300}>Cliente</Table.Column>
          <Table.Column width={320}>Produto</Table.Column>
          <Table.Column width={100}>Cobrança</Table.Column>
          <Table.Column width={320}>Afiliado</Table.Column>
          <Table.Column width={175} align='right'>
            Ganho
          </Table.Column>
        </Table.Header>
        <Table.Body>
          {invoices?.map((invoice, index) => (
            <Table.Row data={invoice} index={index} key={invoice.invoice_id}>
              <Table.Cell>
                <Typography size='xxs'>{invoice.expired_at ? moment(invoice.expired_at).format('DD/MM/YYYY') : '-'}</Typography>
                <Typography size='xxs'>{invoice.expired_at ? moment(invoice.expired_at).format('HH:mm') : '-'}</Typography>
              </Table.Cell>
              <Table.Cell>
                <div className={`${classes.statusCell} ${invoice?.payment_status?.alias || ''}`}>
                  <div>
                    <Typography color='inherit' size='xxs' weight='semibold'>
                      {invoice.id}
                    </Typography>
                    <Typography className={classes.status} color='inherit' size='xxxs' weight='bold'>
                      {invoice?.payment_status?.name || ''}
                    </Typography>
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell>
                <Typography size='xxs' weight='semibold'>
                  <Truncate count={25} final='...'>
                    {subscription?.customer?.business_name || ''}
                  </Truncate>
                </Typography>
                <PermissionHide action='myeduzz:meus-contratos:ver-dados-clientes'>
                  <Typography size='xxs'>
                    <Truncate count={25} final='...'>
                      {subscription?.customer?.email || ''}
                    </Truncate>
                  </Typography>
                </PermissionHide>
              </Table.Cell>
              <Table.Cell>
                <div>
                  <Typography size='xxs' weight='semibold'>
                    <Truncate count={30} final='...'>
                      {invoice?.product?.title?.replace(/^\(\d+\)\s/gi, '') || ''}
                    </Truncate>
                  </Typography>
                  <Typography size='xxs'>({invoice?.product?.id || ''})</Typography>
                </div>
              </Table.Cell>
              <Table.Cell>
                <Typography size='xxs'>{invoice.order + 1}</Typography>
              </Table.Cell>
              <Table.Cell>
                {invoice?.affiliate?.name && (
                  <>
                    <Typography size='xxs' weight='semibold'>
                      <Truncate count={25} final='...'>
                        {invoice?.affiliate?.name || ''}
                      </Truncate>
                    </Typography>
                    <Typography size='xxs'>{invoice?.affiliate?.id || ''}</Typography>
                  </>
                )}
                {!invoice?.affiliate?.name && <span>-</span>}
              </Table.Cell>
              <Table.Cell align='right'>
                <Typography className={classes.gainCell} weight='semibold'>
                  <PermissionHide
                    inverse
                    action={['myeduzz:minhas-vendas:ocultar-valores', 'myeduzz:meus-contratos:ocultar-valores']}
                    fallback={<em>oculto</em>}
                  >
                    <FormattedNumber value={invoice?.net_income || 0} format='BRL' />
                  </PermissionHide>
                  <PaymentImg invoice={invoice} />
                </Typography>
              </Table.Cell>
              <Table.Cell>
                <TableActionButton onClick={() => handleSetCurrentInvoice(invoice)} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Table.Collapse>
  );
});
